import { useDebouncedValue, useLocalStorage } from "@mantine/hooks"
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  Checks,
  DotsThreeVertical,
  List,
  MagnifyingGlass,
  Plus,
  Prohibit,
  Receipt,
  Truck,
  Warning,
  WarningCircle,
  X,
  XCircle,
} from "@phosphor-icons/react"
import {
  Avatar,
  AvatarGroup,
  Button,
  Checkbox,
  CodenameTag,
  InputGroup,
  Menu,
  MenuItem,
  Note,
  Popover,
  SegmentedControl,
  Tag,
  Toast,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import {
  Link,
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { useAuthorization } from "~/components/AuthorizationProvider"
import { Permission } from "~/constants/Permissions"
import { OrdersCustom } from "~/constants/icons"
import { APIRoutes, AppRoutes } from "~/constants/routes"
import { TRADE_KINDS, TwilioMessages } from "~/constants/static.items"
import {
  Order_By,
  Receipt_Bool_Exp,
  UsersQuery,
  useAllReceiptsQuery,
  useAuthenticatedUserQuery,
  useReceiptFinalTrades2Mutation,
} from "~/generated/graphql"
import { usePayableSelector } from "~/pages/d/payable/advance/PayableSelector"
import { config } from "~/pages/pavti/config"
import { downloadPdf } from "~/utils/downloadPdf"

import { openConfirmDialog } from "@vesatogo/grass-confirm-modal"
import { pick } from "lodash-es"
import { renderMultiCommoditySubRow } from "~/components/MultiCommoditySubRow"
import ReceiptActions from "~/components/ReceiptActions"
import FilterTypes from "~/constants/FilterTypes"
import { TradeKinds, hasPendingAdvanceApproval } from "~/constants/Kinds"
import { useReceiptSelectorList } from "~/store/receipts-selector-store"
import ReceiptsFilter from "../ReceiptsFilter"
import SendAdvanceRequestModal from "./SendAdvanceRequestModal"
export function downLoadReceipt(id: string | number) {
  return () => {
    const token = localStorage.getItem("token")
    downloadPdf(APIRoutes.downloadTradeReceipt(id), token).catch(err => {
      return toast.custom(
        <Toast title={"Unable to download the receipt"} intent="danger" />
      )
    })
  }
}

export function addHishobPatti(id?: string[]) {
  const [, receiptFinalTrades] = useReceiptFinalTrades2Mutation()
  const navigate = useNavigate()
  return async () => {
    const { error } = await receiptFinalTrades({
      ids: id,
    })
    if (error) {
      return toast.error(error?.message)
    }
    navigate(`${AppRoutes.tradeReceipts}?updatedAt=true`)
    return toast.success("Successfully added Hishob Patti")
  }
}
const ReceiptMobileCard = ({ original, columns }: any) => {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex flex-col gap-3 mb-3">
      <div className="flex justify-between items-start pb-2 border-b border-gray-100">
        <div className="flex items-start gap-2 max-w-[60%]">
          <div className="pt-1">
            {columns.find((c: any) => c.accessor === "id")?.Cell &&
              (() => {
                const CellComp = columns.find(
                  (c: any) => c.accessor === "id"
                ).Cell
                return <CellComp value={original.id} row={{ original }} />
              })()}
          </div>
          <div className="flex flex-col items-start gap-1">
            {columns.find((c: any) => c.accessor === "name")?.Cell &&
              (() => {
                const CellComp = columns.find(
                  (c: any) => c.accessor === "name"
                ).Cell
                return <CellComp value={original.name} row={{ original }} />
              })()}
            <div className="mt-1">
              {columns.find((c: any) => c.accessor === "kind")?.Cell &&
                (() => {
                  const CellComp = columns.find(
                    (c: any) => c.accessor === "kind"
                  ).Cell
                  return <CellComp value={original.kind} row={{ original }} />
                })()}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-y-4 gap-x-2">
        {columns.map((col: any, idx: number) => {
          if (["id", "name", "kind", "updated_at"].includes(col.accessor))
            return null
          if (col.Header === "") return null

          let val = original
          if (col.accessor) {
            val = col.accessor
              .replace(/\[(\w+)\]/g, ".$1")
              .split(".")
              .reduce((o: any, i: string) => o?.[i], original)
          }
          const CellComp = col.Cell
          return (
            <div key={idx} className="grid grid-cols-2 gap-1 overflow-hidden">
              <span className="text-[11px] text-gray-500 uppercase tracking-wider whitespace-nowrap overflow-hidden text-ellipsis">
                {typeof col.Header === "string" ? col.Header : "Select"}
              </span>
              <div className="text-sm font-500 overflow-hidden text-ellipsis [&>div]:!items-start [&>div]:!text-left">
                {CellComp ? (
                  <CellComp value={val} row={{ original }} />
                ) : (
                  val?.toString() || "--"
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex justify-end items-center gap-3 pt-2 border-t border-gray-100 mt-1">
        {columns
          .filter((c: any) => c.accessor === "updated_at")
          .map((col: any, idx: number) => {
            let val = original
            if (col.accessor) {
              val = col.accessor
                .replace(/\[(\w+)\]/g, ".$1")
                .split(".")
                .reduce((o: any, i: string) => o?.[i], original)
            }
            const CellComp = col.Cell
            return <CellComp key={idx} value={val} row={{ original }} />
          })}
      </div>
    </div>
  )
}

const COMMON_FIELDS = [
  "id",
  "seller",
  "total_amount",
  "total_quantity",
  "commodity",
]

const COLUMNS = [
  {
    accessor: "id",
    Header({ data }) {
      const selectable = data?.filter(d => d.id)
      const { selected, selectAll, reset } = useReceiptSelectorList()
      const isSelectAll =
        Object.keys(selected)?.length &&
        Object.keys(selected)?.length === selectable.length
      return (
        <Checkbox
          labelClassName="!mb-4"
          checked={isSelectAll ? true : false}
          onChange={() => {
            isSelectAll
              ? reset()
              : selectAll(selectable?.map(data => pick(data, COMMON_FIELDS)))
          }}
        />
      )
    },
    Cell({ value, row }) {
      const { selected, setSelected } = useReceiptSelectorList()
      if (!row?.original?.id) return null
      return (
        <Checkbox
          checked={selected[value] ? true : false}
          onChange={() => {
            setSelected(pick(row.original, COMMON_FIELDS))
          }}
        />
      )
    },
  },
  {
    id: "expander",
    Header: "",
    Cell: ({ row }: any) =>
      row?.original?.is_multiple_commodity ? (
        <span
          {...row?.getToggleRowExpandedProps({
            style: { paddingLeft: `${row.depth * 1.5}rem` },
          })}
        >
          {row.isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
        </span>
      ) : null,
  },
  {
    Header: <div className="pl-3">Receipt No</div>,
    accessor: "name",
    Cell({ row: { original } }) {
      if (!original.id) return "--"
      return (
        <div className="pl-4">
          {original?.kind === TradeKinds.Booking &&
          original?.hishob_patti_count?.aggregate?.count > 0 ? (
            <Tag>
              <Link
                className="text-left"
                to={AppRoutes.tradeReceiptDetail(original.id)}
              >
                Receipt #{original.id}
              </Link>
            </Tag>
          ) : (
            <Link
              className="link-text text-left"
              to={AppRoutes.tradeReceiptDetail(original.id)}
            >
              Receipt #{original.id}
            </Link>
          )}
        </div>
      )
    },
  },
  {
    Header: "Type",
    accessor: "kind",
    Cell({ value }) {
      const kind = TRADE_KINDS.find(k => k.id === value)
      return kind?.name || "--"
    },
  },
  {
    Header: "Created On",
    accessor: "created_at",
    Cell({ value }) {
      if (!value) return "--"
      return (
        <Cell
          title={dayjs(value).format("D MMM 'YY")}
          subtitle={dayjs(value).format("hh:mm a")}
        />
      )
    },
  },
  {
    Header: "Supplier",
    accessor: "seller.user[0]",
    Cell({ value }) {
      if (!value) return "--"
      return (
        <Cell
          title={
            <div className="flex items-center gap-1">
              {value.full_name}{" "}
              {value?.is_blocked && (
                <Prohibit color="red" size={20} weight="fill" />
              )}
            </div>
          }
          subtitle={<CodenameTag referenceId={value.external_reference_id} />}
        />
      )
    },
  },
  {
    Header: "Buyers",
    accessor: "trades",
    Cell({ value }) {
      if (!value?.length) return "--"
      return (
        <AvatarGroup>
          {value.map(value => {
            const user = value?.buyer?.user?.[0]
            if (!user) return null
            const blockStyle = user?.is_blocked
              ? {
                  border: "2px solid #F13434",
                  backgroundColor: "#ffe6e6",
                  color: "#F13434",
                }
              : undefined

            return (
              <Tooltip
                content={
                  <div>
                    <div>{user.full_name}</div>
                    <div>{user.external_reference_id}</div>
                  </div>
                }
                key={value.id}
              >
                <Avatar
                  style={blockStyle}
                  name={user.full_name}
                  src={user?.photo?.url}
                />
              </Tooltip>
            )
          })}
        </AvatarGroup>
      )
    },
  },
  {
    Header: "Commodity",
    accessor: "trades",
    id: "commodity",
    Cell({ value, row: { original } }) {
      if (!value) return "--"
      const items = original?.trades?.flatMap(tr => tr?.trade_items)
      const isMultiple = original?.is_multiple_commodity
      const uniqueVarieties = new Set(
        items.map(
          (it: any) => it?.commodity_variety?.id ?? it?.commodity_variety?.name
        )
      )
      const varietyCount = uniqueVarieties.size
      if (isMultiple) {
        return (
          <Cell
            title={
              <Tag intent="info" className="!rounded-xl">
                {varietyCount}
              </Tag>
            }
          />
        )
      }
      return (
        <Cell
          title={items?.[0]?.commodity?.name}
          subtitle={items?.[0]?.commodity_variety?.name}
        />
      )
    },
  },
  {
    Header: "Total Quantity",
    accessor: "total_quantity",
    Cell({ value, row: { original } }) {
      if (!value && value !== 0) return "--"
      return (
        <span>
          {value || "0"}{" "}
          <span className="text-gray-500">
            {original.trades?.[0]?.trade_items[0]?.quantity_unit?.name || "na"}
          </span>
        </span>
      )
    },
  },
  {
    Header: "Total Amount",
    accessor: "total_amount",
    Cell({ value }) {
      if (!value) return "--"
      return <Cell title={inr(value)} />
    },
  },
  {
    Header: "Sub Receipt Count",
    accessor: "child_receipts_aggregate.aggregate.count",
    Cell({ value }) {
      if (!value || value === 0) return "--"
      return (
        <Tag intent="info" pill variant="minimal" className="font-600">
          {value}
        </Tag>
      )
    },
  },
  {
    Header: "Msg Status",
    accessor: "twilio_message.status",
    Cell({ value }) {
      const twilioStatus = TwilioMessages[value]
      if (twilioStatus)
        return (
          <div className="flex gap-1">
            {twilioStatus.id === "failed" ? (
              <X size={20} weight="bold" color={twilioStatus.color} />
            ) : (
              <Checks size={20} weight="bold" color={twilioStatus.color} />
            )}
            {twilioStatus.name}
          </div>
        )
      if (value) return value
      return "--"
    },
  },
  {
    Header: "Advance Draft Pending",
    accessor: "advance_payments",
    id: "status",
    Cell({ value }) {
      const isDraft = value?.find(adv => adv?.status === "Draft")
      if (!isDraft?.status) return "--"
      return isDraft?.status ? (
        <Tooltip content={`Advance Id: #${isDraft?.id}`}>
          <Warning
            size={20}
            weight="fill"
            className="cursor-pointer text-blue"
          ></Warning>
        </Tooltip>
      ) : undefined
    },
  },
  {
    Header: "Placed By",
    accessor: "created_by",
    Cell({ value, row }) {
      if (!value) return "--"
      return (
        <Tooltip
          content={
            <div>
              <p>{value?.full_name || "na"}</p>
              <p className="text-gray-400">
                {dayjs(row?.original?.created_at).format("DD MMM 'YY | h:mm a")}
              </p>
            </div>
          }
        >
          <Avatar
            className="relative text-center"
            name={value?.full_name}
            src={value?.photo?.url}
          />
        </Tooltip>
      )
    },
  },
  {
    Header: "",
    accessor: "advance_payments",
    Cell({ value, row: { original } }) {
      const navigate = useNavigate()
      const setPayableSelected = usePayableSelector(s => s.setSelected)
      const [showAdvanceRequest, setShowAdvanceRequest] = useState(false)
      const createHishobPatti = addHishobPatti([original.id])

      const hasPendingAdvance = value?.some(
        receipt => receipt?.is_verified === null
      )

      const showAdvancePendingError = () => {
        openConfirmDialog({
          isOpen: true,
          bodyClassName: "!min-w-[35rem] w-max",
          title: `Pavti #${original.id}`,
          message: (
            <>
              <div className="flex flex-col items-center justify-center gap-5 mb-3">
                <div className="p-5 rounded-full border-1 border-red-200 bg-red-100">
                  <Warning className="text-red" size={28} />
                </div>
                <span className="text-neutral-600 font-700 text-2xl">
                  Advance Approval Pending
                </span>
              </div>
              <p className="text-gray-800 text-sm px-20 mb-10">
                Child Patti cannot be created until the Sauda/Transit Patti
                advance request is Approved or Rejected.
              </p>
              <Note
                className="text-left text-sm text-neutral-500 !rounded-xl border-1 border-red-200 p-2"
                intent="danger"
                title={
                  (
                    <div className="flex gap-3 items-center">
                      <WarningCircle
                        className="text-red self-start"
                        size={20}
                      />
                      The advance request for this Sauda/Transit Patti is still
                      in approval pending. Please wait for it to be Approved or
                      Rejected to proceed.
                    </div>
                  ) as any
                }
              />
            </>
          ),
          confirmationButtonText: "View advance request",
          primaryButtonProps: { intent: "primary" },
          onSubmit: () => {
            setPayableSelected("receipt")
            return navigate(
              `${AppRoutes.advancePayable}?${createSearchParams({
                search: String(original.id),
              })}`
            )
          },
        })
      }

      return (
        <>
          <Popover
            minimal
            arrow={false}
            className="p-0"
            trigger="click"
            interactive
            animation="fade"
            theme="light-border"
            placement="bottom"
            content={
              <Menu className="max-h-52">
                <MenuItem
                  onClick={() => {
                    navigate(AppRoutes.tradeReceiptDetail(original.id))
                  }}
                >
                  View Details
                </MenuItem>
                <MenuItem onClick={downLoadReceipt(original.id)}>
                  Download Receipt
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (hasPendingAdvance) return showAdvancePendingError()
                    navigate({
                      pathname: AppRoutes.tradeReceiptDetail(original.id),
                      search: `?newfpr=${original.id}`, // newfpr = new from parent receipt query
                    })
                  }}
                >
                  Add New Receipt
                </MenuItem>
                <MenuItem
                  disabled={
                    original?.kind !== TradeKinds.Booking ||
                    original?.hishob_patti_count?.aggregate?.count > 0
                  }
                  onClick={() => {
                    if (hasPendingAdvance) return showAdvancePendingError()
                    return createHishobPatti()
                  }}
                >
                  Add Hishob Patti
                </MenuItem>
                <MenuItem
                  disabled={
                    !original?.advance_payments?.some(
                      adv => adv?.status === "Draft"
                    )
                  }
                  onClick={() => setShowAdvanceRequest(true)}
                >
                  Send Advance Request
                </MenuItem>
              </Menu>
            }
          >
            <button className="hover:bg-gray-300 rounded">
              <DotsThreeVertical size={20} />
            </button>
          </Popover>
          <SendAdvanceRequestModal
            isOpen={showAdvanceRequest}
            onClose={() => setShowAdvanceRequest(false)}
            receiptId={original.id}
          />
        </>
      )
    },
  },
]

export type FilterType = {
  sb_seller?: UsersQuery["base_user"][0]
  sb_buyer?: UsersQuery["base_user"][0]
  com_commodity?: any
  com_variety?: any
  oth_mode?: any
  oth_kind?: any
  oth_status?: any
  oth_facilitator?: any
  oth_created_at?: any
}
const TradeReceipts = () => {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const [{ data: aData }] = useAuthenticatedUserQuery()
  const search = params.get("search")
  const updatedAt = params.get("updatedAt")
  const page = Number(params.get("page")) || 1
  const [filter, setFilter] = useState<FilterType>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.receiptFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  const [debouncedSearch] = useDebouncedValue(search?.trim(), config.debounce)
  const [isMulti, setMulti] = useLocalStorage<boolean>({
    key: "receiptIsMultiCommodity",
    defaultValue: false,
    getInitialValueInEffect: false,
  })
  const canViewTrade = useAuthorization(Permission.ViewTrade)
  const canViewInvoice = useAuthorization(Permission.ViewInvoice)
  const canAddMultiCommodity = useAuthorization(
    Permission.CanAddMultiCommodityTradesAndReceipts
  )
  const buyerCode = filter.sb_buyer?.codename
  const sellerCode = filter.sb_seller?.codename
  const commodityCode = filter.com_commodity?.codename
  const varietyCode = filter.com_variety?.codename
  const facilitatorCode = filter.oth_facilitator?.codename
  const currentDeptCodename = aData?.me?.agent?.current_department?.codename
  const [department] = useLocalStorage({ key: "department" })
  const createdAt = filter?.oth_created_at
  const whereClause: Receipt_Bool_Exp = {
    ...(debouncedSearch
      ? Number(debouncedSearch)
        ? { id: { _eq: Number(debouncedSearch) } }
        : { tracking_code: { _ilike: `%${debouncedSearch}%` } }
      : {}),
    kind: { _eq: filter.oth_kind },
    is_multiple_commodity: {
      _eq: isMulti,
    },
    created_at: createdAt
      ? {
          _lte: dayjs(createdAt).endOf("day").toISOString(),
          _gte: dayjs(createdAt).startOf("day").toISOString(),
        }
      : undefined,
    department: {
      reference_id: {
        _eq: (department && String(department)) || currentDeptCodename,
      },
    },
    seller: sellerCode
      ? {
          reference_id: {
            _eq: sellerCode,
          },
        }
      : undefined,
    trades: {
      buyer: buyerCode
        ? {
            reference_id: {
              _eq: buyerCode,
            },
          }
        : undefined,
    },
    commodity: commodityCode && {
      reference_id: {
        _eq: commodityCode,
      },
    },
    commodity_variety: varietyCode && {
      reference_id: {
        _eq: varietyCode,
      },
    },
    mode: {
      _eq: filter.oth_mode,
    },
    facilitator: facilitatorCode && {
      reference_id: {
        _eq: facilitatorCode,
      },
    },
  }

  const [{ data, fetching }, refetch] = useAllReceiptsQuery({
    variables: {
      limit: config.pageSize,
      offset: (page - 1) * config.pageSize,
      order_by: { created_at: Order_By.DescNullsLast },
      where: whereClause,
    },
    pause: !department && !currentDeptCodename,
    requestPolicy: "network-only",
  })

  useEffect(() => {
    localStorage.setItem(FilterTypes.receiptFilter, JSON.stringify(filter))
  }, [filter])

  const totalReceipts = data?.receipt_aggregate?.aggregate?.count || 0
  useEffect(() => {
    if (updatedAt) {
      refetch()
      params.delete("updatedAt")
      setParams(params)
    }
  }, [updatedAt])

  return (
    <>
      <header className="flex flex-col md:flex-row gap-3 justify-between p-2 border-b-1 border-b-gray-300">
        <div className="overflow-x-auto w-full md:w-auto scrollbar-hide">
          <SegmentedControl
            value={"receipts"}
            onChange={value => {
              if (value === "trades" && canViewTrade) {
                navigate(AppRoutes.tradeRequests)
              } else if (value === "invoices" && canViewInvoice) {
                navigate(AppRoutes.tradeInvoices)
              } else if (value === "trip") {
                navigate(AppRoutes.tradeTrips)
              }
            }}
            data={[
              canViewTrade && {
                label: "Trades",
                value: "trades",
                icon: <List className="mr-1" />,
              },
              {
                label: "Receipts",
                value: "receipts",
                icon: <Receipt className="mr-1" />,
              },
              canViewInvoice && {
                label: "Invoices",
                value: "invoices",
                icon: <OrdersCustom className="mr-1" />,
              },
              {
                label: "Trip",
                value: "trip",
                icon: <Truck className="mr-1" />,
              },
            ].filter(b => b)}
          />
        </div>
        <div className="w-full md:flex-1 md:max-w-md flex items-center gap-3">
          <InputGroup
            className={
              "w-full !text-sm !leading-5 !px-3 !py-[6px] !h-8 md:!text-base md:!leading-6 md:!px-4 md:!py-2 md:!h-10"
            }
            inputProps={{
              value: search || "",
              placeholder: "Search by Receipt no.",
              onChange(e) {
                params.set("search", e.target.value)
                params.delete("page")
                setParams(params)
              },
            }}
            leftElement={<MagnifyingGlass />}
          />
          <ReceiptsFilter filter={filter} setFilter={setFilter} />
        </div>
        <div className="flex flex-wrap gap-3 justify-end w-full md:w-auto items-center !self-center">
          <div className="flex flex-col">
            {canAddMultiCommodity && (
              <Checkbox
                label="Is Multi Commodity"
                value={isMulti as any}
                checked={isMulti}
                onChange={() => setMulti(!isMulti)}
              />
            )}
          </div>
          <ReceiptActions />
          {!department && !currentDeptCodename ? null : (
            <Button
              onClick={() => navigate("new")}
              leftIcon={<Plus />}
              className="text-sm leading-5 px-3 py-[6px] h-8 md:text-base md:leading-6 md:px-4 md:py-2 md:h-10"
            >
              Receipt
            </Button>
          )}
        </div>
      </header>
      <div className="hidden md:block">
        <TableView
          relative={false}
          className="!h-[calc(100vh-64px-57px)]"
          getRowProps={({ original }) => ({
            className:
              original?.seller?.user[0]?.is_blocked ||
              original?.trades?.some(trade => trade?.buyer?.user[0]?.is_blocked)
                ? "bg-red-100 hover:bg-red-100"
                : "",
          })}
          paginationProps={{
            total: Math.ceil(totalReceipts / config.pageSize),
            page: page,
            onChange(page) {
              params.set("page", page.toString())
              params.set("search", "")
              setParams(params)
            },
          }}
          isLoading={fetching}
          columns={COLUMNS}
          data={data?.receipt || []}
          renderRowSubComponent={renderMultiCommoditySubRow(COLUMNS.length, {
            showBuyer: true,
          })}
          meta={<>{totalReceipts} receipts</>}
        />
      </div>
      <div className="md:hidden flex flex-col gap-3 p-3 overflow-y-auto !h-[calc(100vh-64px-57px)] pb-24">
        {fetching ? (
          <div className="text-center py-4 text-gray-500">Loading...</div>
        ) : !data?.receipt || data.receipt.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No Receipts Found
          </div>
        ) : (
          <>
            {data.receipt.map((receipt: any) => (
              <ReceiptMobileCard
                key={receipt.id}
                original={receipt}
                columns={COLUMNS}
              />
            ))}

            {Math.ceil(totalReceipts / config.pageSize) > 1 && (
              <div className="flex justify-between items-center py-4 px-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => {
                    params.set("page", (page - 1).toString())
                    setParams(params)
                  }}
                >
                  Previous
                </Button>
                <span className="text-sm font-500">
                  Page {page} of {Math.ceil(totalReceipts / config.pageSize)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === Math.ceil(totalReceipts / config.pageSize)}
                  onClick={() => {
                    params.set("page", (page + 1).toString())
                    setParams(params)
                  }}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default TradeReceipts
